import React, { Fragment, useState, useEffect } from 'react';
import { Card, Col, Row } from 'reactstrap';
import Charts from 'react-apexcharts';
import { H4, H6 } from '../../../../AbstractElements';
import config from '../../../../config';
import axios from 'axios';
import ConfigDB from '../../../../Config/ThemeConfig';

const TrafficTrend = ({ propertyId, floor, zone, entrance, device }) => {
  const token = localStorage.getItem("sessiontoken");
  const configheader = { headers: { Authorization: `${token}` } };
  const concessionaire_id = localStorage.getItem("concessionaireId");
  const property_access = localStorage.getItem("propertyAccess");

  const primary = localStorage.getItem('default_color') || ConfigDB.data.color.primary_color;
  const secondary = localStorage.getItem('secondary_color') || ConfigDB.data.color.secondary_color;

  const [yesterdaySeries, setYesterdaySeries] = useState([]);
  const [yesterdayCategories, setYesterdayCategories] = useState([]);
  const [lastWeekSeries, setLastWeekSeries] = useState([]);
  const [lastWeekCategories, setLastWeekCategories] = useState([]);
  const [lastMonthSeries, setLastMonthSeries] = useState([]);
  const [lastMonthCategories, setLastMonthCategories] = useState([]);

  useEffect(() => {
    const data = {
      concessionaire_id,
      property_access:property_access,
      property_id: propertyId,
      ...(floor && { floor }),
      ...(zone && { zone }),
      ...(entrance && { entrance_name: entrance }),
      ...(device && { device_name: device }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    axios
      .post(`${config.hostname}/trafficdashboard/gettraffictrend`, data, configheader)
      .then((response) => {
        const { yesterdayInterval, lastWeekInterval, lastMonthDaily } = response.data;

        setYesterdaySeries([{ name: 'Yesterday', data: yesterdayInterval.map((i) => i.enters) }]);
        setYesterdayCategories(yesterdayInterval.map((i) => i.timeRange));

        setLastWeekSeries([{ name: 'Last Week', data: lastWeekInterval.map((i) => i.enters) }]);
        setLastWeekCategories(lastWeekInterval.map((i) => i.date));

        setLastMonthSeries([{ name: 'Last Month', data: lastMonthDaily.map((i) => i.enters) }]);
        setLastMonthCategories(lastMonthDaily.map((i) => i.date));
      })
      .catch((err) => console.error(err));
  }, [propertyId, floor, zone, entrance, device]);

  const baseChartOptions = {
    chart: {
      type: 'area',
      height: 170,
      toolbar: { show: false },
      zoom: { enabled: false },
      dropShadow: {
        enabled: true,
        top: 3,
        left: 0,
        blur: 3,
        opacity: 0.15,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.9,
        opacityFrom: 0.7,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    // REMOVE POINTS ON GRAPH
    markers: {
      size: 0,           // hides the points
      strokeWidth: 0,
      hover: { size: 0 },
    },
    grid: {
      borderColor: '#f0f0f0',
      strokeDashArray: 3,
      padding: { left: 0, right: 0, bottom: 0, top: 10 },
    },
    tooltip: {
      theme: 'light',
      style: { fontSize: '12px' },
      x: { show: true },
      y: {
        formatter: (val) => val?.toLocaleString(),
        title: { formatter: () => 'Visitors: ' },
      },
    },
    yaxis: { labels: { show: false } },
    xaxis: {
      type: 'category',
      tickPlacement: 'on',
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    annotations: {
      yaxis: [
        {
          y: 0,
          y2: 100,
          borderColor: primary,
          fillColor: 'rgba(115,102,255,0.05)',
          opacity: 0.1,
          label: { show: false },
        },
      ],
    },
    responsive: [
      {
        breakpoint: 576,
        options: {
          chart: { height: 120 },
          markers: { size: 0 },
        },
      },
    ],
  };

  const chartYesterday = {
    series: yesterdaySeries,
    options: {
      ...baseChartOptions,
      colors: ['#4caaf0'],
      xaxis: { ...baseChartOptions.xaxis, categories: yesterdayCategories },
    },
  };

  const chartLastWeek = {
    series: lastWeekSeries,
    options: {
      ...baseChartOptions,
      colors: [primary],
      xaxis: { ...baseChartOptions.xaxis, categories: lastWeekCategories },
    },
  };

  const chartLastMonth = {
    series: lastMonthSeries,
    options: {
      ...baseChartOptions,
      colors: ['#4caaf0'],
      xaxis: { ...baseChartOptions.xaxis, categories: lastMonthCategories },
    },
  };

  const getTotal = (series) =>
    series[0]?.data?.reduce((a, b) => a + b, 0)?.toLocaleString() || '0';

  return (
    <Fragment>
      <Row>
        {/* Yesterday */}
        <Col xl='4' md='12' className='box-col-12'>
          <Card className='o-hidden'>
            <div className='chart-widget-top'>
              <Row className='row card-body pb-0 m-0'>
                <Col xl='9' lg='8' className='col-9 p-0'>
                  <H6 attrH6={{ className: 'mb-2' }}>Traffic Yesterday</H6>
                  <H4>{getTotal(yesterdaySeries)}</H4>
                  <span>Hourly Trend</span>
                </Col>
              </Row>
              <div id='chart-widget1'>
                <Charts {...chartYesterday} height='200' type='area' />
              </div>
            </div>
          </Card>
        </Col>

        {/* Last Week */}
        <Col xl='4'>
          <Card className='o-hidden'>
            <div className='chart-widget-top'>
              <Row className='card-body pb-0 m-0'>
                <Col xl='9' lg='8' xs='9' className='p-0'>
                  <H6 attrH6={{ className: 'mb-2' }}>Traffic Last Week</H6>
                  <H4>{getTotal(lastWeekSeries)}</H4>
                  <span>Daily Trend</span>
                </Col>
              </Row>
              <div id='chart-widget2'>
                <Charts {...chartLastWeek} height='200' type='area' />
              </div>
            </div>
          </Card>
        </Col>

        {/* Last Month */}
        <Col xl='4'>
          <Card className='o-hidden'>
            <div className='chart-widget-top'>
              <Row className='card-body pb-0 m-0'>
                <Col xl='9' lg='8' xs='9' className='p-0'>
                  <H6 attrH6={{ className: 'mb-2' }}>Traffic Last Month</H6>
                  <H4>{getTotal(lastMonthSeries)}</H4>
                  <span>Daily Trend</span>
                </Col>
              </Row>
              <div id='chart-widget3'>
                <Charts {...chartLastMonth} height='200' type='area' />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default TrafficTrend;
